import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from 'contexts/AuthContext';
import axiosInstance from 'config/axiosInstance';
import Icon from 'components/AppIcon';
import Button from 'components/ui/Button';

const AdminPaymentDetails = () => {
  const navigate = useNavigate();
  const { paymentId } = useParams();
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloadingInvoice, setDownloadingInvoice] = useState(false);
  const [processingRefund, setProcessingRefund] = useState(false);
  const { user, isAuthenticated } = useAuth();

  useEffect(() => {
    if (!isAuthenticated || user?.role !== 'admin') {
      navigate('/admin/login');
      return;
    }
    fetchPaymentDetails();
  }, [paymentId, isAuthenticated, user]);

  const fetchPaymentDetails = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await axiosInstance.get(
        `/payments/admin/payments/${paymentId}/`
      );

      const paymentData = response.data;
      console.log('Admin payment data received:', paymentData);

      setPaymentDetails(paymentData);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching payment details:', error);
      setError('Failed to load payment details. Please try again.');
      setLoading(false);
    }
  };

  
  const handleDownloadReceipt = async () => {
    if (!paymentId) {
      alert('Payment information not available');
      return;
    }

    try {
      setDownloadingInvoice(paymentId);
      
      const response = await axiosInstance.get(
        `/payments/${paymentId}/invoice/`,
        { 
       
          responseType: 'blob'
        }
      );
      
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Invoice_${orderId}.pdf`);
      document.body.appendChild(link);
      link.click();
      
      link.remove();
      window.URL.revokeObjectURL(url);
      
    } catch (error) {
      console.error('Error downloading invoice:', error);
      alert('Failed to download invoice. Please try again or contact support.');
    } finally {
      setDownloadingInvoice(null);
    }
  };

  const handleProcessRefund = async () => {
    if (!paymentDetails?.id) return;

    try {
      setProcessingRefund(true);
      
      // Navigate to refund creation page with payment ID
      navigate(`/admin/refunds/create?payment=${paymentDetails.id}`);
      
    } catch (error) {
      console.error('Error processing refund:', error);
      alert('Failed to process refund. Please try again.');
    } finally {
      setProcessingRefund(false);
    }
  };

  const handleContactUser = () => {
    if (paymentDetails?.user_email) {
      window.open(`mailto:${paymentDetails.user_email}?subject=Regarding Payment ${paymentDetails.transaction_id}`, '_blank');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatPrice = (amount, currency = 'USD') => {
    const symbols = {
      'USD': '$',
      'EUR': '€',
      'GBP': '£',
      'INR': '₹',
      'CAD': 'C$',
      'AUD': 'A$'
    };
    return `${symbols[currency] || '$'}${parseFloat(amount).toFixed(2)}`;
  };

  const getPaymentMethodLabel = (method) => {
    const labels = {
      'razorpay': 'Razorpay',
      'paypal': 'PayPal',
      'stripe': 'Stripe',
      'cashfree': 'Cashfree',
      'payu': 'PayU'
    };
    return labels[method] || method;
  };

  const getStatusBadge = (status) => {
    const styles = {
      completed: 'bg-green-100 text-green-800 border border-green-200',
      pending: 'bg-yellow-100 text-yellow-800 border border-yellow-200',
      failed: 'bg-red-100 text-red-800 border border-red-200',
      refunded: 'bg-gray-100 text-gray-800 border border-gray-200'
    };

    const labels = {
      completed: 'Completed',
      pending: 'Pending',
      failed: 'Failed',
      refunded: 'Refunded'
    };

    return (
      <span className={`px-3 py-1 text-sm font-medium rounded-full ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  const getAccessTypeLabel = (accessType) => {
    const labels = {
      'liveOne': 'Live - Single',
      'liveGroup': 'Live - Group',
      'recordedOne': 'Recorded - Single',
      'recordedGroup': 'Recorded - Group',
      'comboOne': 'Combo - Single',
      'comboGroup': 'Combo - Group',
      'live': 'Live Access',
      'recorded': 'Recorded Access',
      'combo': 'Live + Recorded'
    };
    return labels[accessType] || accessType;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-100 rounded-full mb-4 animate-pulse">
              <Icon name="Clock" size={32} className="text-blue-600" />
            </div>
            <p className="text-gray-600">Loading payment details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !paymentDetails) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 pt-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center max-w-md mx-auto">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-full mb-4">
              <Icon name="XCircle" size={32} className="text-red-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Payment</h2>
            <p className="text-gray-600 mb-6">{error || 'Payment information not found'}</p>
            <div className="space-x-4">
              <Button onClick={() => navigate('/admin/payments')} variant="default">
                Back to Payments
              </Button>
              <Button onClick={fetchPaymentDetails} variant="outline">
                Try Again
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/admin/payments')}
                iconName="ArrowLeft"
                className="text-gray-600 hover:text-gray-900"
              >
                Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Payment Details</h1>
                <p className="text-gray-600">Transaction ID: {paymentDetails.transaction_id}</p>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              {getStatusBadge(paymentDetails.status)}
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Payment Information */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Payment Information</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Transaction ID</label>
                    <p className="text-lg font-mono text-gray-900">{paymentDetails.transaction_id}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Amount</label>
                    <p className="text-2xl font-bold text-blue-600">
                      {formatPrice(paymentDetails.amount, paymentDetails.currency)}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Payment Method</label>
                    <p className="text-gray-900">{getPaymentMethodLabel(paymentDetails.payment_method)}</p>
                  </div>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <label className="text-sm font-medium text-gray-500">Created Date</label>
                    <p className="text-gray-900">{formatDate(paymentDetails.created_at)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Completed Date</label>
                    <p className="text-gray-900">{formatDate(paymentDetails.completed_at) || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Gateway Order ID</label>
                    <p className="text-gray-900 font-mono text-sm">{paymentDetails.gateway_order_id || 'N/A'}</p>
                  </div>
                </div>
              </div>

              {paymentDetails.failure_reason && (
                <div className="mt-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <Icon name="AlertTriangle" size={20} className="text-red-500" />
                    <span className="font-medium text-red-800">Failure Reason</span>
                  </div>
                  <p className="text-red-700 mt-1">{paymentDetails.failure_reason}</p>
                </div>
              )}
            </div>

            {/* User Information */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">User Information</h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium text-gray-500">User Name</label>
                  <p className="text-gray-900">{paymentDetails.user_name || 'N/A'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Email</label>
                  <p className="text-gray-900">{paymentDetails.user_email}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Platform</label>
                  <p className="text-gray-900">{paymentDetails.platform_info?.name || 'Default'}</p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-500">Platform ID</label>
                  <p className="text-gray-900 font-mono text-sm">{paymentDetails.platform_info?.platform_id || 'default'}</p>
                </div>
              </div>
            </div>

            {/* Webinar Details */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h2 className="text-xl font-semibold text-gray-900 mb-6">Webinar Details</h2>
              
              <div className="space-y-4">
                {paymentDetails.payment_webinars?.map((webinar, index) => (
                  <div key={index} className="flex space-x-4 pb-4 border-b border-gray-200 last:border-0">
                    <div className="w-20 h-16 bg-gray-200 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name="Video" size={24} className="text-gray-400" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">
                        {webinar.webinar_title}
                      </h3>
                      <p className="text-gray-600 text-sm mb-1">
                        Webinar ID: {webinar.webinar_id}
                      </p>
                      <p className="text-gray-600 text-sm">
                        Access Type: {getAccessTypeLabel(webinar.access_type)}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="text-lg font-semibold text-gray-900">
                        {formatPrice(webinar.amount, paymentDetails.currency)}
                      </div>
                      <p className="text-xs text-gray-500">
                        {formatDate(webinar.created_at)}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Sidebar Actions */}
          <div className="space-y-6">
            {/* Admin Actions */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Admin Actions</h3>
              
              <div className="space-y-3">
                <Button
                  variant="default"
                  onClick={handleDownloadReceipt}
                  iconName={downloadingInvoice ? "Loader2" : "Download"}
                  iconPosition="left"
                  className="w-full"
                  disabled={downloadingInvoice}
                >
                  {downloadingInvoice ? 'Downloading...' : 'Download Invoice'}
                </Button>

                {paymentDetails.status === 'completed' && (
                  <Button
                    variant="outline"
                    onClick={handleProcessRefund}
                    iconName={processingRefund ? "Loader2" : "RotateCcw"}
                    iconPosition="left"
                    className="w-full text-yellow-600 border-yellow-600 hover:bg-yellow-600 hover:text-white"
                    disabled={processingRefund}
                  >
                    {processingRefund ? 'Processing...' : 'Process Refund'}
                  </Button>
                )}

                <Button
                  variant="outline"
                  onClick={handleContactUser}
                  iconName="Mail"
                  iconPosition="left"
                  className="w-full"
                >
                  Contact User
                </Button>

                <Button
                  variant="outline"
                  onClick={() => navigate(`/admin/users/${paymentDetails.user_id}`)}
                  iconName="User"
                  iconPosition="left"
                  className="w-full"
                >
                  View User Profile
                </Button>
              </div>
            </div>

            {/* Transaction Details */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Transaction Details</h3>
              
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Invoice Number:</span>
                  <span className="font-mono text-gray-900">{paymentDetails.invoice_display_number}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Gateway Payment ID:</span>
                  <span className="font-mono text-gray-900">{paymentDetails.gateway_payment_id || 'N/A'}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Currency:</span>
                  <span className="text-gray-900">{paymentDetails.currency}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Last Updated:</span>
                  <span className="text-gray-900">{formatDate(paymentDetails.updated_at)}</span>
                </div>
              </div>
            </div>

            {/* Quick Links */}
            <div className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Links</h3>
              
              <div className="space-y-3">
                <Button
                  variant="ghost"
                  onClick={() => navigate('/admin/payments')}
                  iconName="List"
                  iconPosition="left"
                  className="w-full justify-start text-gray-700 hover:text-gray-900"
                >
                  All Payments
                </Button>

                <Button
                  variant="ghost"
                  onClick={() => navigate('/admin/refunds')}
                  iconName="RotateCcw"
                  iconPosition="left"
                  className="w-full justify-start text-gray-700 hover:text-gray-900"
                >
                  Refund Management
                </Button>

                <Button
                  variant="ghost"
                  onClick={() => navigate('/admin/reports')}
                  iconName="BarChart3"
                  iconPosition="left"
                  className="w-full justify-start text-gray-700 hover:text-gray-900"
                >
                  Financial Reports
                </Button>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminPaymentDetails;